/* ===================================================================
   THE FLOAT CLUB — fc-attrib.js
   First-party traffic attribution. No cookies, no fingerprinting, no
   PII. Captures where a visitor came from and hands it to the checkout
   hand-off (fc-commerce.js) so it lands on the Shopify order, and to
   fc-track.js so pixels can tag events with it.

   TWO TOUCHES, KEPT IN localStorage under "fc_attrib":
     first — set once on this browser, never overwritten
     last  — updated whenever a NEW source shows up: an explicit
             utm_source / click id / ?ref=, or a non-empty referrer
             from another site. A plain click around the site itself
             (no query params, referrer = this same host) leaves last
             untouched rather than flattening it to "direct".

   Load this BEFORE fc-commerce.js (defer scripts run in document
   order), so window.FC_ATTRIB is ready when the checkout button is
   wired up.
=================================================================== */
(function (global) {
  "use strict";

  var STORAGE_KEY = "fc_attrib";
  var CLICK_IDS = ["fbclid", "ttclid", "gclid", "msclkid"];
  var CLICK_ID_SOURCE = { fbclid: "facebook", ttclid: "tiktok", gclid: "google", msclkid: "bing" };
  var CLICK_ID_MEDIUM = { fbclid: "paid_social", ttclid: "paid_social", gclid: "cpc", msclkid: "cpc" };

  function safeGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function safeSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }

  function parseQuery(search) {
    var out = {};
    if (!search) return out;
    var qs = search.charAt(0) === "?" ? search.slice(1) : search;
    qs.split("&").forEach(function (pair) {
      if (!pair) return;
      var idx = pair.indexOf("=");
      var k = idx === -1 ? pair : pair.slice(0, idx);
      var v = idx === -1 ? "" : pair.slice(idx + 1);
      try {
        k = decodeURIComponent(k.replace(/\+/g, " "));
        v = decodeURIComponent(v.replace(/\+/g, " "));
      } catch (e) { return; }
      out[k] = v;
    });
    return out;
  }

  function hostOf(url) {
    if (!url) return "";
    try { return new URL(url).hostname.toLowerCase(); } catch (e) {}
    var m = url.match(/^https?:\/\/([^\/]+)/i);
    return m ? m[1].toLowerCase() : "";
  }

  // hostname -> source label. Order matters: check the more specific
  // strings first so nothing else collides with them.
  function classifyReferrer(ref) {
    var host = hostOf(ref);
    if (!host) return "direct";
    if (host.indexOf("instagram") !== -1) return "instagram";
    if (host.indexOf("facebook") !== -1 || host.indexOf("fb.") !== -1) return "facebook";
    if (host.indexOf("tiktok") !== -1) return "tiktok";
    if (host.indexOf("google") !== -1) return "google-organic";
    if (host.indexOf("bing") !== -1) return "bing-organic";
    if (host.indexOf("t.co") !== -1 || host.indexOf("twitter") !== -1 || host.indexOf("x.com") !== -1) return "x";
    if (host.indexOf("youtube") !== -1) return "youtube";
    if (host.indexOf("pinterest") !== -1) return "pinterest";
    return "referral:" + host;
  }

  function mediumForClassified(source) {
    if (source === "direct") return "";
    if (source === "google-organic" || source === "bing-organic") return "organic";
    if (source.indexOf("referral:") === 0) return "referral";
    return "social"; // instagram, facebook, tiktok, x, youtube, pinterest
  }

  function device() {
    try {
      return (global.matchMedia && global.matchMedia("(max-width: 820px)").matches) ? "mobile" : "desktop";
    } catch (e) { return "desktop"; }
  }

  // What this page load looks like, and whether it carries explicit
  // attribution (query params) vs an inferred one (referrer only).
  function buildTouch() {
    var q = parseQuery(global.location.search);
    var source, medium, campaign, explicit = false;

    if (q.utm_source) {
      source = q.utm_source;
      medium = q.utm_medium || "";
      campaign = q.utm_campaign || "";
      explicit = true;
    } else {
      var clickKey = null;
      for (var i = 0; i < CLICK_IDS.length; i++) {
        if (q[CLICK_IDS[i]]) { clickKey = CLICK_IDS[i]; break; }
      }
      if (clickKey) {
        source = CLICK_ID_SOURCE[clickKey];
        medium = CLICK_ID_MEDIUM[clickKey];
        campaign = q.utm_campaign || "";
        explicit = true;
      } else if (q.ref) {
        source = q.ref;
        medium = "referral";
        campaign = q.utm_campaign || "";
        explicit = true;
      }
    }

    if (!explicit) {
      var ref = (global.document && global.document.referrer) || "";
      source = classifyReferrer(ref);
      medium = mediumForClassified(source);
      campaign = q.utm_campaign || "";
    }

    return {
      source: source,
      medium: medium || "",
      campaign: campaign || "",
      content: q.utm_content || "",
      term: q.utm_term || "",
      ts: new Date().toISOString(),
      landing: (global.location.pathname + global.location.search).slice(0, 300),
      device: device(),
      explicit: explicit
    };
  }

  function isExternalReferrer(ref) {
    var refHost = hostOf(ref);
    if (!refHost) return false;
    return refHost !== global.location.hostname.toLowerCase();
  }

  function loadState() {
    var raw = safeGet(STORAGE_KEY);
    if (!raw) return null;
    try {
      var o = JSON.parse(raw);
      return (o && typeof o === "object" && o.first && o.last) ? o : null;
    } catch (e) { return null; }
  }

  function run() {
    var touch = buildTouch();
    var refExternal = false;
    if (!touch.explicit) {
      var ref = (global.document && global.document.referrer) || "";
      refExternal = !!ref && isExternalReferrer(ref);
    }

    var state = loadState();
    if (!state) {
      state = { first: touch, last: touch };
    } else if (touch.explicit || refExternal) {
      state.last = touch;
    }
    // else: internal navigation or a bare direct reload — last stays as-is

    try { safeSet(STORAGE_KEY, JSON.stringify(state)); } catch (e) {}

    global.FC_ATTRIB = {
      get: function () { return { first: state.first, last: state.last }; }
    };
  }

  if (global.document.readyState === "loading") {
    global.document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }

})(window);

/* ===================================================================
   THE FLOAT CLUB — self-owned traffic beacon
   Fires ONE anonymous visit event per browser session to Ben's own
   collector (tfc-beacon on Vercel), so "where does traffic come from"
   can be answered from first-party infra. No PII: no IP storage, no
   fingerprinting, no cookies — sessionStorage only gates whether this
   session has already reported in. Any failure here is silent; the
   site must never notice this code exists.
=================================================================== */
(function (global) {
  "use strict";

  if (!global.location || global.location.hostname !== "thefloatclub.store") return;

  var COLLECTOR_URL = "https://tfc-beacon.vercel.app/api/t";
  var SENT_KEY = "fc_beacon_sent";

  function safeSessionGet(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function safeSessionSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }

  function sendBeacon() {
    try {
      if (safeSessionGet(SENT_KEY)) return;
      if (!global.FC_ATTRIB || typeof global.FC_ATTRIB.get !== "function") return;

      var touch = global.FC_ATTRIB.get();
      var last = touch && touch.last;
      var first = touch && touch.first;
      if (!last || !first) return;

      // The first-touch record is written once, ever, the very first
      // time fc_attrib has no prior state. If that timestamp is
      // effectively "now", this page load is the one that created
      // it — a brand new visitor, not a returning one.
      var firstTs = Date.parse(first.ts);
      var newVisitor = !isNaN(firstTs) && (Date.now() - firstTs) < 10000;

      /* Owner flag: Ben visits the site once per device with ?owner=1 and
         that browser marks itself forever. Owner sessions still record,
         but the report can split them out, so his own checks never read
         as customers again. */
      var owner = false;
      try {
        if (/[?&]owner=1\b/.test(global.location.search)) localStorage.setItem("fc_owner", "1");
        owner = localStorage.getItem("fc_owner") === "1";
      } catch (e) {}

      var event = {
        ts: new Date().toISOString(),
        source: last.source || "",
        medium: last.medium || "",
        campaign: last.campaign || "",
        landing: last.landing || "",
        path: (global.location.pathname || "").slice(0, 300),
        device: last.device || "",
        newVisitor: newVisitor,
        owner: owner
      };

      var body = JSON.stringify(event);
      var sent = false;

      if (global.navigator && typeof global.navigator.sendBeacon === "function") {
        try { sent = global.navigator.sendBeacon(COLLECTOR_URL, body); } catch (e) { sent = false; }
      }

      if (!sent && global.fetch) {
        try {
          global.fetch(COLLECTOR_URL, { method: "POST", body: body, keepalive: true, mode: "cors" });
        } catch (e) {}
      }

      safeSessionSet(SENT_KEY, "1");
    } catch (e) {
      // Beacon must never break the site.
    }
  }

  if (global.document.readyState === "loading") {
    global.document.addEventListener("DOMContentLoaded", sendBeacon);
  } else {
    sendBeacon();
  }

})(window);
